import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, MessagesSquare, Pencil } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useConnector, useConnectorConversations, useConnectorTypes } from "@/api/queries/connectors"
import { PageContainer, PageHeader } from "@/components/app/page"
import { EmptyState, ErrorState } from "@/components/app/states"
import { Button } from "@/components/ui/button"
import { ConnectorStatusBadge } from "@/features/connectors/connector-status"
import { ConversationList, Transcript } from "@/features/connectors/conversations"
import { useConnectorEvents } from "@/features/connectors/use-connector-events"
import { cn } from "@/lib/utils"

interface ConversationsSearch {
  /** Selected thread (`context_id`). */
  context?: string
}

export const Route = createFileRoute("/_app/studio/connectors/$connectorId")({
  staticData: { titleKey: "studio.connectors.conversations.title" },
  validateSearch: (s: Record<string, unknown>): ConversationsSearch => ({
    context: typeof s.context === "string" && s.context ? s.context : undefined,
  }),
  component: ConversationsPage,
})

function BackButton() {
  const { t } = useTranslation()
  return (
    <Button variant="outline" size="sm" nativeButton={false} render={<Link to="/studio/connectors" />}>
      <ArrowLeft />
      {t("studio.connectors.conversations.back")}
    </Button>
  )
}

function ConversationsPage() {
  const { t } = useTranslation()
  const { connectorId } = Route.useParams()
  const id = Number(connectorId)
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  // live status and new turns; polling only while the feed is down
  const live = useConnectorEvents() === "open"
  const connector = useConnector(id, !live)
  const types = useConnectorTypes()
  const conversations = useConnectorConversations(id, !live)

  const select = (context: string | undefined) =>
    void navigate({ search: (prev) => ({ ...prev, context }), replace: !!context === !!search.context })

  if (connector.isError)
    return (
      <PageContainer>
        <ErrorState error={connector.error} onRetry={() => void connector.refetch()} className="mt-10" />
        <div className="mt-4 flex justify-center">
          <BackButton />
        </div>
      </PageContainer>
    )

  const c = connector.data
  const typeLabel = c ? (types.data?.find((s) => s.type === c.type)?.label ?? c.type) : ""
  const selected = conversations.data?.find((x) => x.context_id === search.context)

  return (
    <PageContainer width="wide">
      <PageHeader
        icon={MessagesSquare}
        title={c ? c.name : t("studio.connectors.conversations.title")}
        documentTitle={c ? `${c.name} · ${t("studio.connectors.conversations.title")}` : undefined}
        badge={c && <ConnectorStatusBadge connector={c} typeLabel={typeLabel} />}
        description={
          c
            ? [typeLabel, c.agent_name ?? t("studio.connectors.noAgent")].join(" · ")
            : t("studio.connectors.conversations.description")
        }
        actions={
          <>
            <BackButton />
            {c && (
              <Button
                variant="outline"
                size="sm"
                nativeButton={false}
                render={<Link to="/studio/connectors" search={{ connector: c.id }} />}
              >
                <Pencil />
                {t("common.edit")}
              </Button>
            )}
          </>
        }
      />
      <p className="text-muted-foreground -mt-3 mb-4 text-sm">
        {t("studio.connectors.conversations.description")}
      </p>
      <div className="grid gap-4 lg:h-[calc(100dvh-16rem)] lg:min-h-[28rem] lg:grid-cols-[20rem_minmax(0,1fr)]">
        <ConversationList
          className={cn("max-lg:max-h-[70dvh]", selected && "max-lg:hidden")}
          conversations={conversations.data}
          selected={search.context}
          onSelect={select}
          loading={conversations.isPending}
          error={conversations.error}
          onRetry={() => void conversations.refetch()}
        />
        {selected ? (
          <Transcript
            className="max-lg:min-h-[60dvh]"
            connectorId={id}
            conversation={selected}
            agentLabel={c?.agent_name ?? ""}
            live={live}
            onBack={() => select(undefined)}
            onForgotten={() => select(undefined)}
          />
        ) : (
          <div className="bg-card hidden items-center justify-center rounded-xl border lg:flex">
            <EmptyState
              variant="plain"
              icon={MessagesSquare}
              title={t("studio.connectors.conversations.select.title")}
              description={t("studio.connectors.conversations.select.description")}
            />
          </div>
        )}
      </div>
    </PageContainer>
  )
}
